import React, { useCallback, useEffect, useState } from 'react'
import './App.css'
import { parseQuiz, QText, Question, Quiz } from './quiz'
import Confetti from 'react-confetti'
import useWindowDimensions from './useWindowDimensions'

function isSolved (question: QText[], guesses: string[]) {
  for (let idx = 0; idx < question.length; ++idx) {
    const q = question[idx]
    if (q.hidden && !isCorrect(guesses[idx], q.text)) {
      return false
    }
  }
  return true
}

function * correctOnly (question: QText[], guesses: string[]):Generator<string> {
  for (let idx = 0; idx < question.length; ++idx) {
    const q = question[idx]
    if (q.hidden && isCorrect(guesses[idx], q.text)) {
      yield q.text
    } else {
      yield ''
    }
  }
}

function initGuesses (question:Question, prefill?: string[]) {
  return question.map((v, idx) => prefill?.[idx] ?? '')
}

function isCorrect (guess: string, correction:string) {
  function clean (a:string) {
    return a.toLowerCase()
      .replaceAll('.', '')
      .replaceAll(',', '')
      .replaceAll(' ', '')
      .replaceAll('-', '')
      .replaceAll('\'', '')
  }

  const fIdx = clean(correction).indexOf(clean(guess))
  return guess.length > 0 && fIdx === 0
}

function App () {
  const [state, setState] = useState<string>('menu')
  const [quiz, setQuiz] = useState<Quiz>([])
  const [questionIdx, setQuestionIdx] = useState(0)
  const [score, setScore] = useState(0)
  const [unsolved, setUnsolved] = useState<Question[]>([])
  const [guesses, setGuesses] = useState<string[]>([])
  const [showAnswer, setShowAnswer] = useState<boolean>(false)
  const [prefill, setPrefill] = useState<string[][]>([])
  const [nextPrefill, setNextPrefill] = useState<string[][]>([])

  function handleFileChange (files: FileList | null) {
    if (files == null) {
      return
    }
    const file = files.item(0)
    if (file == null) {
      return
    }
    const fr = new FileReader()
    fr.onload = () => {
      const content:string = fr.result as string
      const quiz = parseQuiz(content)
      if (quiz.length > 0) {
        launchQuiz(quiz)
      }
    }
    fr.readAsText(file, 'UTF8')
  }

  function reset (quiz:Quiz) {
    launchQuiz(quiz, [])
  }
  function retry (unsolved: Quiz) {
    console.log(unsolved, nextPrefill)
    launchQuiz(unsolved, nextPrefill)
  }

  function launchQuiz (quiz:Quiz, prefilled: string[][] = []) {
    setState('quiz')
    setQuiz(quiz)
    setQuestionIdx(0)
    setScore(0)
    setUnsolved([])
    setShowAnswer(false)
    setGuesses(initGuesses(quiz[0], prefilled[0]))
    setNextPrefill([])
    setPrefill(prefilled)
  }

  const handleNext = useCallback(() => {
    if (!showAnswer) {
      setShowAnswer(true)
    } else {
      const solved = isSolved(quiz[questionIdx], guesses)
      if (solved) {
        setScore(score + 1)
      } else {
        setUnsolved([...unsolved, quiz[questionIdx]])
        setNextPrefill([...nextPrefill, [...correctOnly(quiz[questionIdx], guesses)]])
      }
      setShowAnswer(false)
      if (questionIdx + 1 < quiz.length) {
        setGuesses(
          initGuesses(quiz[questionIdx + 1], prefill[questionIdx + 1])
        )
        setQuestionIdx(questionIdx + 1)
      } else {
        setState('results')
      }
    }
  }, [guesses, questionIdx, quiz, score, showAnswer, unsolved, prefill, nextPrefill])

  function handleGuess (idx: number, value: string) {
    const newGuesses = [...guesses]
    newGuesses[idx] = value
    setGuesses(newGuesses)
  }

  const { width, height } = useWindowDimensions()
  useEffect(() => {
    const keyDownHandler = (event:KeyboardEvent) => {
      console.log('User pressed: ', event.key)

      if (event.key === 'Enter') {
        if (showAnswer) {
          handleNext()
        }
      }
    }

    document.addEventListener('keydown', keyDownHandler)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [handleNext, showAnswer])

  return (
    <div className='App'>
      <header className='App-header'>
        <h1 className='title'>Quizogen Pro</h1>
      </header>

      {state === 'menu' && (
        <div className='input-container'>
          <h3>Select questions file</h3>
          <input
            className='input-box'
            accept='.txt'
            type='file'
            onChange={e => handleFileChange(e.target.files)}
          >
          </input>
        </div>
      )}
      {state === 'results' && quiz != null && (
        <>
          <p>Score: {score} / {quiz.length}</p>
          <Confetti width={width - 40} height={height} numberOfPieces={score === quiz.length ? 180 : 0} />
          <div className='button-container'>
            {score < quiz.length && (
              <button onClick={() => retry(unsolved)} className='next-button'>Retry failed questions</button>
            )}
            <button onClick={() => reset(quiz)} className='next-button'>Restart</button>
            <button onClick={() => setState('menu')} className='next-button'>Load new</button>
          </div>
        </>
      )}
      {state === 'quiz' && quiz != null && (
        <div className='quiz-container'>
          <form
            onSubmit={(e) => {
              handleNext()
              e.preventDefault()
            }} className='quiz'
          >
            <h3>Question {questionIdx + 1} / {quiz.length}</h3>
            {quiz[questionIdx].map((e, idx) => (
              <span className='text-node' key={idx}>
                {!showAnswer && (
                  <>
                    {e.hidden && (
                      <input type='text' value={guesses[idx]} onChange={e => handleGuess(idx, e.target.value)} />
                    )}
                    {!e.hidden && e.text}
                  </>
                )}
                {showAnswer && (
                  <>
                    {e.hidden && isCorrect(guesses[idx], e.text) && (
                      <>
                        <span className='correct'>{e.text}</span>
                        <span className='light-bulb' title='I got this wrong' onClick={() => handleGuess(idx, '')}>💡</span>
                      </>
                    )}
                    {e.hidden && !isCorrect(guesses[idx], e.text) && (
                      <>
                        <span className='incorrect'>{guesses[idx] !== '' ? guesses[idx] : '""'}</span>
                        {' '}
                        <span className='actual'>{e.text}</span>
                        <span className='light-bulb' title='Give me the point' onClick={() => handleGuess(idx, e.text)}>💡</span>
                      </>
                    )}
                    {!e.hidden && e.text}
                  </>
                )}
              </span>
            ))}

            <div className='button-container'>
              <button type='submit' className='next-button'>Next</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default App
